/**
 * PWAInstallPrompt.js
 * PWA 설치 프롬프트 컴포넌트
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
  useTheme,
} from '@mui/material';
import { X, Download } from 'react-feather';

const PWAInstallPrompt = () => {
  const theme = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // beforeinstallprompt 이벤트 리스너
    const handleBeforeInstallPrompt = (e) => {
      // 기본 동작 방지
      e.preventDefault();
      // 이벤트 저장
      setDeferredPrompt(e);
      
      // 이전에 설치를 거부하지 않았다면 프롬프트 표시
      const installPromptDismissed = localStorage.getItem('pwa-install-prompt-dismissed');
      if (!installPromptDismissed) {
        setShowPrompt(true);
      }
    };

    // 앱이 이미 설치되어 있는지 확인
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIOSStandalone = isIOS && (window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches);

    if (!isStandalone && !isIOSStandalone) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    // 프롬프트 표시
    deferredPrompt.prompt();

    // 사용자 선택 대기
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('[PWA] 사용자가 설치를 수락했습니다.');
    } else {
      console.log('[PWA] 사용자가 설치를 거부했습니다.');
    }

    // 프롬프트 정리
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // 오늘 하루 동안 다시 표시하지 않음
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    localStorage.setItem('pwa-install-prompt-dismissed', tomorrow.toISOString());
  };

  // 만료된 dismiss 상태 확인
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('pwa-install-prompt-dismissed');
    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil);
      if (dismissedDate < new Date()) {
        localStorage.removeItem('pwa-install-prompt-dismissed');
      }
    }
  }, []);

  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          maxWidth: 400,
          margin: '0 auto',
          zIndex: 10000,
          borderRadius: 3,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(140deg, rgba(32, 34, 42, 0.98) 0%, rgba(18, 19, 26, 0.95) 100%)'
            : '#FFFFFF',
          backdropFilter: theme.palette.mode === 'dark' ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: theme.palette.mode === 'dark' ? 'blur(20px)' : 'none',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(158, 164, 189, 0.3)' : 'rgba(221, 225, 230, 0.9)'}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 28px 48px -18px rgba(0, 0, 0, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
            : '0 12px 28px -16px rgba(15, 29, 68, 0.15)',
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(123, 104, 238, 0.2) 0%, rgba(181, 165, 255, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(165, 148, 249, 0.15) 0%, rgba(123, 104, 238, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Download
                  size={20}
                  color={theme.palette.mode === 'dark' ? '#B5A5FF' : '#7B68EE'}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                앱 설치
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            OnTimeLog를 앱으로 설치하면 브라우저가 꺼져있어도 알림을 받을 수 있습니다.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleDismiss}
              sx={{
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              나중에
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleInstall}
              sx={{
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(123, 104, 238, 0.9) 0%, rgba(181, 165, 255, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(123, 104, 238, 0.95) 0%, rgba(165, 148, 249, 0.9) 100%)',
                '&:hover': {
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(181, 165, 255, 0.95) 100%)'
                    : 'linear-gradient(135deg, rgba(123, 104, 238, 1) 0%, rgba(165, 148, 249, 1) 100%)',
                },
              }}
            >
              설치하기
            </Button>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default PWAInstallPrompt;

